import { useState } from 'react';

const features = [
  { icon: '🛡️', title: '实名可信', desc: '身份核验，保障安全，求职者与企业双向实名认证' },
  { icon: '🎤', title: '声纹打卡', desc: '声纹识别技术，防止代打卡，确保本人到岗' },
  { icon: '📍', title: '定位到岗', desc: 'GPS定位签到，确认到岗时间与地点' },
  { icon: '📋', title: '证据留痕', desc: '全程记录可追溯，处理工资及履约争议有据可依' },
  { icon: '🤖', title: '智能匹配', desc: '基于技能与位置的智能岗位推荐' },
  { icon: '⭐', title: '信用体系', desc: '双方互评，建立可信的零工信用档案' },
];

const workerSteps = ['浏览岗位', '登录绑定手机与微信', '完善身份与技能', '报名面试', '到岗打卡', '完工结算评价'];
const employerSteps = ['了解平台', '注册企业账户', '完成企业认证', '发布岗位与班次', '选择人员', '验收到岗结算评价'];

export default function LandingPage() {
  const [activeTab, setActiveTab] = useState<'worker' | 'employer'>('worker');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-teal-500 to-blue-600 px-4 pb-20 pt-16 text-white sm:px-6 lg:px-8">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-[-10rem] top-[-10rem] h-[25rem] w-[25rem] rounded-full bg-white/10 blur-[5rem]" />
          <div className="absolute right-[-8rem] top-1/3 h-[20rem] w-[20rem] rounded-full bg-blue-300/10 blur-[5rem]" />
        </div>
        <div className="relative mx-auto max-w-4xl text-center">
          <h1 className="text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">早知零工调度平台</h1>
          <p className="mt-4 text-xl font-bold text-emerald-100 sm:text-2xl">可信用工 · 智能调度 · 安心结算</p>
          <p className="mx-auto mt-6 max-w-2xl text-base text-white/80 sm:text-lg">
            连接求职者与企业，提供安全可靠的零工撮合服务。
            实名认证、声纹打卡、智能匹配，让每一份零工都有保障。
          </p>
          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <a href="/login" className="inline-flex h-14 w-full items-center justify-center rounded-2xl bg-white px-10 text-lg font-bold text-emerald-600 shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl sm:w-auto">
              浏览岗位
            </a>
            <a href="/login" className="inline-flex h-14 w-full items-center justify-center rounded-2xl border-2 border-white/30 bg-white/10 px-10 text-lg font-bold text-white backdrop-blur transition-all hover:bg-white/20 sm:w-auto">
              登录 / 注册
            </a>
          </div>
        </div>
      </section>

      {/* Featured Jobs Preview */}
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <h2 className="text-center text-2xl font-bold text-slate-900">今日热门岗位</h2>
        <p className="mt-2 text-center text-slate-500">登录后可查看更多岗位详情</p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { title: '门店促销员', pay: '25/h', loc: '2.3km', tag: '附近' },
            { title: '活动签到引导', pay: '220/天', loc: '3.1km', tag: '热门' },
            { title: '餐饮晚峰补位', pay: '30/h', loc: '1.6km', tag: '急招' },
            { title: '快递分拣员', pay: '20/h', loc: '4.2km', tag: '长期' },
            { title: '家教辅导', pay: '80/h', loc: '1.8km', tag: '精选' },
            { title: '活动摄影', pay: '300/天', loc: '5.0km', tag: '新单' },
          ].map((job, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
              <div className="flex items-start justify-between">
                <h3 className="text-lg font-bold text-slate-900">{job.title}</h3>
                <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">{job.tag}</span>
              </div>
              <div className="mt-3 flex items-center gap-4 text-sm text-slate-500">
                <span>📍 {job.loc}</span>
                <span>💰 {job.pay}</span>
              </div>
              <button className="mt-4 w-full rounded-xl bg-slate-100 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-emerald-100 hover:text-emerald-700">
                查看详情
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl font-bold text-slate-900">为什么选择早知零工</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <div key={i} className="rounded-2xl border border-slate-100 bg-slate-50 p-6 transition-shadow hover:shadow-md">
                <div className="text-3xl">{f.icon}</div>
                <h3 className="mt-4 text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl font-bold text-slate-900">如何使用</h2>
          <div className="mt-6 flex justify-center gap-4">
            <button onClick={() => setActiveTab('worker')} className={'rounded-full px-6 py-2 text-sm font-medium transition ' + (activeTab === 'worker' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600')}>求职者</button>
            <button onClick={() => setActiveTab('employer')} className={'rounded-full px-6 py-2 text-sm font-medium transition ' + (activeTab === 'employer' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600')}>企业</button>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(activeTab === 'worker' ? workerSteps : employerSteps).map((step, i) => (
              <div key={i} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-lg font-bold text-emerald-600">{i + 1}</div>
                <span className="text-sm font-medium text-slate-700">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-emerald-500 to-teal-600 py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">准备好开始了吗？</h2>
          <p className="mt-4 text-emerald-100">立即注册，开启你的零工之旅</p>
          <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <a href="/login" className="inline-flex h-14 items-center justify-center rounded-2xl bg-white px-10 text-lg font-bold text-emerald-600 shadow-lg transition-all hover:-translate-y-1">我要揾工</a>
            <a href="/login" className="inline-flex h-14 items-center justify-center rounded-2xl border-2 border-white/30 bg-white/10 px-10 text-lg font-bold text-white backdrop-blur transition-all hover:bg-white/20">我要招人</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="mx-auto max-w-6xl px-4 text-center text-sm text-slate-500 sm:px-6 lg:px-8">
          <p>早知零工调度平台 · 连接每一份可信的零工</p>
          <p className="mt-2">© 2025 Zaogig. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
